import React, { useState, useEffect, useRef } from "react";
import { Input, InputGroup, InputGroupText } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  addItem,
  updateCartItems,
} from "../../ReduxToolkit/Reducers/CartReducer";
import {
  addProduct,
  setLoading,
  setProduct,
} from "../../ReduxToolkit/Reducers/ProductsReducer";
import { getAPIURL, getVendorSettings } from "../../services/helper";
import http from "../../services/http";
import shortcutManager from "../../services/shortcutManager";
import POSAddonModal from "./POSAddonModal";
import POSItem from "./POSItem";
import {
  normalizeAddons,
  applyTierDiscountToProduct,
  promptForVariableProductPrice,
} from "./Helper";

function POSItems({ onSearchInputRef }) {
  const dispatch = useDispatch();
  const { products, categories } = useSelector((state) => state.products);
  const { vendorCustomers } = useSelector((state) => state.app);
  const { customer_id, cartItems, posItems } = useSelector(
    (state) => state.cart,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addonQuantities, setAddonQuantities] = useState({});
  const [modal, setModal] = useState(false);
  const searchInputRef = useRef(null);
  const api_url = getAPIURL();

  // Pass search input ref to parent
  useEffect(() => {
    if (onSearchInputRef && searchInputRef.current) {
      onSearchInputRef(searchInputRef.current);
    }
  }, [onSearchInputRef]);

  // Apply tier discounts when customer changes
  useEffect(() => {
    if (!cartItems?.length || !customer_id) return;

    const tierSettings = getVendorSettings("tierDiscounts");
    const updatedItems = cartItems.map((item) => {
      if (item.cart_only) return item;
      return applyTierDiscountToProduct(
        item,
        customer_id,
        vendorCustomers,
        categories,
        tierSettings,
      );
    });

    dispatch(updateCartItems(updatedItems));
  }, [customer_id, vendorCustomers, categories, dispatch]);

  // Register shortcuts
  useEffect(() => {
    shortcutManager.unregisterContext("pos");

    // Search focus
    shortcutManager.register("/", () => searchInputRef.current?.focus(), "pos");

    // Clear search on escape
    shortcutManager.register(
      "Escape",
      () => {
        if (
          searchInputRef.current &&
          document.activeElement === searchInputRef.current
        ) {
          setSearchQuery("");
          searchInputRef.current.blur();
          return true;
        }
        return false;
      },
      "pos",
    );

    // Product shortcuts
    posItems.forEach((product) => {
      if (product.shortcut_key && !modal) {
        shortcutManager.register(
          product.shortcut_key,
          () => handleProductClick(product),
          "pos",
        );
      }
    });

    shortcutManager.startListening();
    return () => shortcutManager.unregisterContext("pos");
  }, [posItems, modal]);

  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const handleKeyPress = async (e) => {
    if (e.key !== "Enter") return;

    const query = searchQuery.toLowerCase();

    // Check if input is a number (price)
    const isNumeric = /^\d+(\.\d+)?$/.test(searchQuery.trim());

    if (isNumeric) {
      const price = parseFloat(searchQuery);

      const { value: itemName } = await Swal.fire({
        title: `Add item with price $${price}`,
        input: "text",
        inputLabel: "Item Name",
        inputPlaceholder: "Enter item name (e.g., pizza)",
        showCancelButton: true,
        confirmButtonText: "Add to Cart",
        inputValidator: (value) => {
          if (!value) {
            return "Please enter an item name";
          }
        },
      });

      if (itemName) {
        const customItem = {
          id: `custom_${Date.now()}`,
          name: itemName,
          price: price,
          regular_price: price,
          quantity: 1,
          cart_only: true,
          taxable: true,
        };

        dispatch(addItem(customItem));
        setSearchQuery("");
        toast.success(`${itemName} added to cart with price $${price}`);
      }
      return;
    }

    const exactMatch = products.find(
      (p) =>
        (p.sku && p.sku.toLowerCase() === query) ||
        p.name.toLowerCase() === query,
    );

    if (exactMatch) {
      handleProductClick(exactMatch);
      setSearchQuery("");
      return;
    }

    if (!searchQuery.trim()) return;

    const { isConfirmed } = await Swal.fire({
      title: "No product found!",
      text: "Do you want to add a new product?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add it!",
    });

    if (!isConfirmed) return;

    const { value: productData } = await Swal.fire({
      title: "Add New Product",
      html: `
        <input id="sku" class="swal2-input" placeholder="SKU" value="${searchQuery}">
        <input id="name" class="swal2-input" placeholder="Product Name">
        <input id="price" class="swal2-input" placeholder="Product Price">
        <input id="quantity" class="swal2-input" placeholder="Quantity" type="number">
      `,
      focusConfirm: false,
      preConfirm: () => ({
        sku: document.getElementById("sku").value,
        name: document.getElementById("name").value,
        regular_price: document.getElementById("price").value,
        stock_quantity: parseInt(document.getElementById("quantity").value, 10),
      }),
    });

    if (!productData) return;

    try {
      dispatch(setLoading(true));
      const resp = await dispatch(addProduct(productData)).unwrap();
      if (resp?.success) {
        toast.success("Product successfully added");
        handleProductClick(resp.data);
        setSearchQuery("");
      }
    } catch (error) {
      toast.error("Failed to add product: " + error.message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    setSelectedProduct(null);
    setAddonQuantities({});
  };

  const handleProductClick = async (product) => {
    // Apply vendor price override
    const overrides = getVendorSettings("pos_price_overrides") || {};
    if (overrides[product.id] !== undefined) {
      product = { ...product, price: overrides[product.id], regular_price: overrides[product.id] };
    }

    if (product.manage_stock && product.stock_quantity < 1) {
      return toast.error("Item out of stock");
    }

    const addonsArray = normalizeAddons(product.addons);

    if (addonsArray.length > 0) {
      setSelectedProduct({ ...product, addons: addonsArray });
      const initialQuantities = addonsArray.reduce((acc, addon) => {
        if (addon.type === "radio") acc[addon.id] = "";
        else if (addon.type === "select") {
          acc[addon.id] = addon.options.reduce((optAcc, option) => {
            optAcc[option.id] = 0;
            return optAcc;
          }, {});
        }
        return acc;
      }, {});
      setAddonQuantities(initialQuantities);
      setModal(true);
      return;
    }

    // Variable price product
    if (product.is_pos_variable === "yes") {
      const pricedProduct = await promptForVariableProductPrice(product);
      if (!pricedProduct) return;

      const finalProduct = applyTierDiscountToProduct(
        { ...pricedProduct, quantity: 1 },
        customer_id,
        vendorCustomers,
        categories,
        getVendorSettings("tierDiscounts"),
      );
      return dispatch(addItem(finalProduct));
    }

    if (parseFloat(product.regular_price) <= 0) {
      return toast.error("Item amount not set");
    }

    const finalProduct = applyTierDiscountToProduct(
      { ...product, quantity: 1 },
      customer_id,
      vendorCustomers,
      categories,
      getVendorSettings("tierDiscounts"),
    );

    dispatch(addItem(finalProduct));
  };

  const handleAddToCartWithAddons = (selectedAddons, subtotal) => {
    if (selectedProduct.manage_stock && selectedProduct.stock_quantity < 1) {
      return toast.error("Item out of stock");
    }

    if (parseFloat(subtotal) <= 0) {
      return toast.error("Product price cannot be zero");
    }

    const baseProduct = {
      ...selectedProduct,
      quantity: 1,
      addons: selectedAddons,
      _basePrice: parseFloat(selectedProduct.price) || 0,
      _addonDefinitions: selectedProduct.addons,
      _addonQuantities: addonQuantities,
      price: subtotal,
    };

    const finalProduct = applyTierDiscountToProduct(
      baseProduct,
      customer_id,
      vendorCustomers,
      categories,
      getVendorSettings("tierDiscounts"),
    );

    dispatch(addItem(finalProduct));
    toggleModal();
  };

  return (
    <div>
      <InputGroup size="lg">
        <InputGroupText className="list-light-warning">
          <i className="icofont icofont-search txt-warning"></i>
        </InputGroupText>
        <Input
          innerRef={searchInputRef}
          type="text"
          placeholder="Search products by SKU"
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyUp={handleKeyPress}
        />
      </InputGroup>

      <POSItem
        searchQuery={searchQuery}
        products={products}
        onProductClick={handleProductClick}
      />

      {selectedProduct && (
        <POSAddonModal
          modal={modal}
          toggleModal={toggleModal}
          product={selectedProduct}
          addonQuantities={addonQuantities}
          setAddonQuantities={setAddonQuantities}
          handleAddToCartWithAddons={handleAddToCartWithAddons}
        />
      )}
    </div>
  );
}

export default POSItems;
